import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Modal, StyleSheet, Text, TouchableOpacity, View } from './rn';
import { fontSize, radius, spacing } from '../theme';
import { useTheme } from '../theme/ThemeContext';
import { useTranslation } from '../i18n/I18nContext';
import { openLocationMap, validMapPoint } from '../navigation/locationMaps';
export { mapsUrl } from '../navigation/locationMaps';

export type LivePosition = {
  latitude: number | null;
  longitude: number | null;
  accuracy_m: number | null;
  point_at: string | null;
};

/** Konumun ne kadar taze olduğu; sayı değil, okunur bir aralık. */
export function freshnessLabel(pointAt: string | null, now: Date): string | null {
  if (!pointAt) return null;
  const an = new Date(pointAt).getTime();
  if (!Number.isFinite(an)) return null;
  const sn = Math.max(0, Math.round((now.getTime() - an) / 1000));
  if (sn < 60) return `${sn}s`;
  if (sn < 3600) return `${Math.floor(sn / 60)}dk`;
  return `${Math.floor(sn / 3600)}sa`;
}

/** Panelin konumu ne sıklıkla sorduğu. Sorma aynı zamanda "bakıyorum" demek. */
export const PANEL_POLL_SECONDS = 15;

type Props = {
  visible: boolean;
  shareId: number;
  fetchPosition: (shareId: number) => Promise<LivePosition>;
  onClose: () => void;
  now?: () => Date;
};

/**
 * İzleyenin konum paneli (9.3.3.5 · D-584).
 *
 * Panel açık olduğu sürece konumu soruyor ve bu sorma, paylaşan cihaza
 * "biri bakıyor" diyen sinyalin ta kendisi (9.3.3.4). Panel kapandığında
 * sorma da bitiyor; kimsenin bir şey kapatması gerekmiyor, karşı taraftaki
 * ölçüm kendiliğinden seyrekleşiyor.
 *
 * Harita burada ÇİZİLMİYOR: gömülü canlı harita native modül ister (9.3.3.7).
 * Onun yerine sistem haritasına çıkış var — bugün çalışan, yarın da doğru
 * kalacak bir kapı.
 */
export default function LiveLocationPanel({
  visible, shareId, fetchPosition, onClose, now,
}: Props) {
  const colors = useTheme();
  const { t } = useTranslation();
  const [position, setPosition] = useState<LivePosition | null>(null);
  const canli = useRef(false);

  // Soran işlev bir REF'te tutuluyor, bağımlılık olarak değil. Ekran her
  // çizimde yeni bir ok işlevi veriyor; efekt ona bağlı olsaydı her çizimde
  // yeniden kurulur ve her kurulum anında bir soru daha sorardı. Cihazda
  // böyle görüldü: 15 saniyede bir sorması gereken panel 5 saniyede bir, hatta
  // aynı saniyede dört kez soruyordu — üstelik bu sorular karşı tarafın
  // ölçmeye devam etmesini de tetikliyor.
  const soran = useRef(fetchPosition);
  soran.current = fetchPosition;

  const sor = useCallback(async (id: number) => {
    try {
      const p = await soran.current(id);
      if (canli.current) setPosition(p);
    } catch {
      // Tek bir başarısız soru paneli kapatmıyor; bir sonraki tur yeniden sorar.
    }
  }, []);

  useEffect(() => {
    if (!visible) return;
    canli.current = true;
    setPosition(null);
    void sor(shareId);
    const sayac = setInterval(() => { void sor(shareId); }, PANEL_POLL_SECONDS * 1000);
    return () => { canli.current = false; clearInterval(sayac); };
  }, [visible, shareId, sor]);

  const simdi = (now ?? (() => new Date()))();
  const tazelik = freshnessLabel(position?.point_at ?? null, simdi);
  const varMi = validMapPoint(position?.latitude, position?.longitude);

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.overlay}>
        <View style={[styles.sheet, { backgroundColor: colors.white }]}>
          <Text style={[styles.title, { color: colors.gray400 }]}>
            {t('message.liveLocationCardTitle')}
          </Text>
          <Text testID="live-panel-position" style={[styles.position, { color: colors.black }]}>
            {varMi
              ? `${position!.latitude!.toFixed(5)}, ${position!.longitude!.toFixed(5)}`
              : t('message.liveLocationNoPoint')}
          </Text>
          {tazelik && (
            <Text testID="live-panel-freshness" style={[styles.meta, { color: colors.gray600 }]}>
              {t('message.liveLocationUpdatedAgo', { ago: tazelik })}
            </Text>
          )}
          {/* 9.3.3.6 — izlenmek tek yönlü değil, ve bu izleyenden gizlenmiyor.
              Paylaşan bakışı görüyorsa, bakan da bunu bilmeli. */}
          <Text testID="live-panel-mutual" style={[styles.meta, { color: colors.gray400 }]}>
            {t('message.liveLocationSharerSeesYou')}
          </Text>
          <View style={styles.actions}>
            <TouchableOpacity testID="live-panel-close" style={styles.action} onPress={onClose}>
              <Text style={[styles.actionText, { color: colors.gray600 }]}>{t('common.cancel')}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              testID="live-panel-maps"
              disabled={!varMi}
              style={[styles.action, styles.primary, { backgroundColor: colors.black }, !varMi && styles.disabled]}
              onPress={async () => {
                if (!varMi) return;
                try {
                  await openLocationMap(position!.latitude!, position!.longitude!);
                } catch {
                  Alert.alert(t('message.locationTitle'), t('message.locationMapError'));
                }
              }}>
              <Text style={[styles.actionText, { color: colors.white }]}>
                {t('message.liveLocationOpenInMaps')}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: { flex: 1, justifyContent: 'flex-end', backgroundColor: 'rgba(0,0,0,0.35)' },
  sheet: {
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.lg,
    paddingBottom: spacing.xl,
    borderTopLeftRadius: radius.layerOuter,
    borderTopRightRadius: radius.layerOuter,
    alignItems: 'center',
  },
  title: { fontSize: fontSize.sm },
  position: { fontSize: fontSize.lg, fontWeight: '700', marginTop: spacing.sm },
  meta: { fontSize: fontSize.sm, marginTop: spacing.xs },
  actions: { flexDirection: 'row', gap: spacing.md, marginTop: spacing.lg, alignSelf: 'stretch' },
  action: { flex: 1, minHeight: 48, borderRadius: radius.control, alignItems: 'center', justifyContent: 'center' },
  primary: {},
  disabled: { opacity: 0.4 },
  actionText: { fontSize: fontSize.md, fontWeight: '600' },
});
